import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { AppHelper } from 'src/app/shared/utilities/app.helper';

import { SvgIconComponent } from '@ngneat/svg-icon';
import { Popover, PopoverModule } from 'primeng/popover';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { passingExtendInfoService } from 'src/app/shared/services/passingExtendInfo.service';
import { AppConstant } from 'src/app/shared/utilities/app.constant';
import { StringNameEmailPipe } from '../../../shared/pipes/string/string-showNameEmail.pipe';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-extend-info',
  templateUrl: './extend-info.component.html',
  styleUrls: ['./extend-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PopoverModule, StringNameEmailPipe, SvgIconComponent],
})
export class ExtendInfoComponent implements OnInit, OnDestroy {
  @ViewChild('contactPopover') contactPopover!: Popover;

  passingData: any = { primaryContact: [] };
  isEmptyData: boolean = false;

  /** Click event to re-show once the popover finishes closing for a target change. */
  private pendingShowEvent: Event | null = null;

  constructor(
    private _passingDataService: passingExtendInfoService,
    private _notificationService: NotificationService,
    private _cd: ChangeDetectorRef
  ) {}

  private destroy$ = new Subject<void>();

  ngOnInit() {
    this._passingDataService.currentApprovalStageMessage
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        // An empty `primaryContact` with no accompanying click `event` is a
        // transitional/placeholder update (e.g. contacts still loading) and
        // is safe to ignore. When an `event` IS present though, it must
        // still be processed so the popover can be hidden/deferred-reshown
        // at the new target even while the contact list is momentarily empty.
        if (!data || (data.primaryContact?.length === 0 && !data.event)) {
          return;
        }

        this.passingData = data;
        this.isEmptyData = typeof data.primaryContact === 'string';

        if (data.primaryContact === 'Loading') {
          this.pendingShowEvent = null;
          this.contactPopover?.hide();
        } else if (data.event) {
          if (this.contactPopover?.overlayVisible) {
            // Already open for a different trigger: close first, then reopen
            // at the new target once the hide animation completes.
            this.pendingShowEvent = data.event;
            this.contactPopover.hide();
          } else {
            this.contactPopover?.show(data.event);
          }
        }

        this._cd.detectChanges();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onPopoverHide(): void {
    if (this.pendingShowEvent) {
      const event = this.pendingShowEvent;
      this.pendingShowEvent = null;
      this.contactPopover.show(event);
    }
  }

  // (click)="makeCall('+84 929087540')"
  makeCall(phoneNumber: string): void {
    this.navigateTo(`tel:${phoneNumber}`);
  }

  /**
   * Isolated so tests can spy on navigation without touching the real
   * `window.location` global: browsers mark `Location` members as
   * `[Unforgeable]`, so `Object.defineProperty`/`spyOnProperty` cannot
   * stub `window.location.href` in real browsers (Karma/Chrome throws
   * `TypeError: Cannot redefine property: location/href`).
   */
  private navigateTo(url: string): void {
    window.location.href = url;
  }

  // (click)="makeCallMsTeams('ABC@gmail.com')"
  makeCallMsTeams(phoneNumber: string, isSameDomain: boolean): void {
    if (!isSameDomain) {
      return;
    }
    const teamsUrl = `https://teams.microsoft.com/l/call/0/0?users=${phoneNumber}`;
    window.open(teamsUrl, '_blank');
  }

  // (click)="makeMailMsOutlook('thanh.nguyencong@halliburton.com', 'One', 'Two')"
  makeMailMsOutlook(
    emailAddress: string,
    subjectMail: string,
    bodyContent: string
  ) {
    const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(
      emailAddress
    )}&subject=${encodeURIComponent(subjectMail)}&body=${encodeURIComponent(
      bodyContent
    )}`;
    // Use Outlook web app if Outlook app is not installed
    window.open(outlookUrl, '_blank');
  }

  // Checking Same Domain
  checkDomain(emailAddressLogin: string, emailAddressCompare: string): boolean {
    if (emailAddressLogin && emailAddressCompare) {
      let emailAddressLoginDomain = 'halliburton.com';
      let emailAddressCompareDomain = emailAddressCompare.split('@')[1];
      return emailAddressLoginDomain.toLocaleLowerCase() ===
        emailAddressCompareDomain.toLocaleLowerCase()
        ? true
        : false;
    } else {
      return false;
    }
  }
  // copy text
  copyText(copyText: string) {
    navigator.clipboard.writeText(copyText);
    this._notificationService.setMessage({
      type: AppConstant.MESSAGE_TYPE.SUCCESS,
      header: 'Copy clipboard !',
      content: `Copied: ${copyText} successfully`,
    });
  }

  filterPrimaryContact(listDataContact: any[]) {
    if (listDataContact && listDataContact.length) {
      listDataContact = listDataContact.map((contact: any) => {
        return {
          ...contact,
          PHONE: AppHelper.UtileFunctions.replaceAllCharacter(
            contact.PHONE,
            '-',
            ''
          ).trim(),
        };
      });

      let take3Sort = [...listDataContact];
      take3Sort.sort(function (a, b) {
        var x = a.ORDER.toLowerCase();
        var y = b.ORDER.toLowerCase();
        return x < y ? -1 : x > y ? 1 : 0;
      });
      return take3Sort;
    } else {
      return listDataContact;
    }
  }
}
